import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { clearToken } from "../lib/api";

const AuthContext = createContext(null);
const HTTP_UNAUTHORIZED = 401;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      if (e?.response?.status !== HTTP_UNAUTHORIZED && process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.error("auth/me failed:", e?.message || e);
      }
      // 401 → token (if any) is dead. Clear it so the interceptor stops
      // shipping a bad header on every subsequent request.
      if (e?.response?.status === HTTP_UNAUTHORIZED) clearToken();
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // On every mount (page reload, tab reopen, PWA cold-start) hit /auth/me.
    // If the 30-day HttpOnly cookie OR Bearer token is still valid, the user
    // is restored immediately and RedirectIfAuthed in App.js will send them
    // straight to /dashboard — no login page flicker.
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("logout error:", e?.message);
      }
    }
    // Clear the persisted Bearer token. The cookie is already cleared by the
    // backend logout endpoint. Doing this AFTER the network call guarantees
    // the request itself was authed.
    clearToken();
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, setUser, loading, refresh, logout }), [user, loading, refresh, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
